import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { API_ENDPOINTS } from '../config/api';

interface Booking {
  id: string;
  user_id: string;
  locker_id: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  total_amount: number;
  created_at: string;
  updated_at: string;
  users: {
    full_name: string;
    email: string;
  };
  lockers: {
    locker_number: string;
    locations: {
      name: string;
      address: string;
    };
    locker_sizes: {
      name: string;
      hourly_rate: number;
      daily_rate: number;
    };
  };
}

const AdminBookingManagement: React.FC = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchBookings();
    }
  }, [user, selectedStatus]);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const url = selectedStatus === 'all'
        ? `${API_ENDPOINTS.BASE}/api/bookings/admin/all`
        : `${API_ENDPOINTS.BASE}/api/bookings/admin/status/${selectedStatus}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(Array.isArray(data) ? data : []);
      } else {
        toast.error('Failed to fetch bookings');
      }
    } catch (error) {
      toast.error('Error fetching bookings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (bookingId: string, newStatus: string) => {
    if (!confirm(`Are you sure you want to change this booking status to ${newStatus}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Booking status updated to ${newStatus}`);
        fetchBookings();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Status update failed');
      }
    } catch (error) {
      toast.error('Error updating booking status');
    }
  };

  const handleViewDetails = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

    if (diffHours < 24) {
      return `${diffHours} hour(s)`;
    } else {
      const diffDays = Math.ceil(diffHours / 24);
      return `${diffDays} day(s)`;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusActions = (booking: Booking) => {
    switch (booking.status) {
      case 'pending':
        return (
          <>
            <button
              onClick={() => handleStatusChange(booking.id, 'active')}
              className="text-green-600 hover:text-green-900 mr-2"
            >
              Activate
            </button>
            <button
              onClick={() => handleStatusChange(booking.id, 'cancelled')}
              className="text-red-600 hover:text-red-900"
            >
              Cancel
            </button>
          </>
        );
      case 'active':
        return (
          <button
            onClick={() => handleStatusChange(booking.id, 'completed')}
            className="text-blue-600 hover:text-blue-900"
          >
            Complete
          </button>
        );
      default:
        return <span className="text-gray-500">No actions</span>;
    }
  };

  if (user?.role !== 'admin') {
    return <div className="p-6">Access denied. Admin privileges required.</div>;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Booking Management</h1>
        <div className="text-sm text-gray-600">
          Total Bookings: {bookings.length}
        </div>
      </div>

      {/* Status Filter */}
      <div className="mb-6">
        <div className="flex space-x-2">
          <button
            onClick={() => setSelectedStatus('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              selectedStatus === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setSelectedStatus('pending')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              selectedStatus === 'pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setSelectedStatus('active')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              selectedStatus === 'active'
                ? 'bg-green-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setSelectedStatus('completed')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              selectedStatus === 'completed'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => setSelectedStatus('cancelled')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              selectedStatus === 'cancelled'
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Cancelled
          </button>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location & Locker
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {booking.users.full_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {booking.users.email}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {booking.lockers.locations.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      Locker {booking.lockers.locker_number} ({booking.lockers.locker_sizes.name})
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>
                    <div>{formatDate(booking.start_time)}</div>
                    <div className="text-gray-500">
                      {formatDuration(booking.start_time, booking.end_time)}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  BWP {booking.total_amount.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                    {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleViewDetails(booking)}
                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                  >
                    Details
                  </button>
                  {getStatusActions(booking)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Booking Details Modal */}
      {showDetailsModal && selectedBooking && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Booking Details
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Customer Information</h4>
                    <p className="text-sm text-gray-600">Name: {selectedBooking.users.full_name}</p>
                    <p className="text-sm text-gray-600">Email: {selectedBooking.users.email}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Locker Information</h4>
                    <p className="text-sm text-gray-600">Location: {selectedBooking.lockers.locations.name}</p>
                    <p className="text-sm text-gray-600">Locker: {selectedBooking.lockers.locker_number}</p>
                    <p className="text-sm text-gray-600">Size: {selectedBooking.lockers.locker_sizes.name}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Booking Details</h4>
                    <p className="text-sm text-gray-600">Start: {formatDate(selectedBooking.start_time)}</p>
                    <p className="text-sm text-gray-600">End: {formatDate(selectedBooking.end_time)}</p>
                    <p className="text-sm text-gray-600">Duration: {formatDuration(selectedBooking.start_time, selectedBooking.end_time)}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Financial</h4>
                    <p className="text-sm text-gray-600">Total Amount: BWP {selectedBooking.total_amount.toFixed(2)}</p>
                    <p className="text-sm text-gray-600">Hourly Rate: BWP {selectedBooking.lockers.locker_sizes.hourly_rate}</p>
                    <p className="text-sm text-gray-600">Daily Rate: BWP {selectedBooking.lockers.locker_sizes.daily_rate}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900">Status Management</h4>
                  <div className="flex space-x-2 mt-2">
                    {selectedBooking.status === 'pending' && (
                      <>
                        <button
                          onClick={() => {
                            handleStatusChange(selectedBooking.id, 'active');
                            setShowDetailsModal(false);
                          }}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          Activate
                        </button>
                        <button
                          onClick={() => {
                            handleStatusChange(selectedBooking.id, 'cancelled');
                            setShowDetailsModal(false);
                          }}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {selectedBooking.status === 'active' && (
                      <button
                        onClick={() => {
                          handleStatusChange(selectedBooking.id, 'completed');
                          setShowDetailsModal(false);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Complete
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookingManagement;
